import Promise from 'bluebird';
import _ from 'lodash';
import moment from 'moment';
import worldMap from '../core/map';
import { pinBuilder } from '../core/map/pinBuilder';
import ColorBrewer from '../core/tools/colorbrewer';
import environment from '../environment';
import templates from '../core/templates';
import translate from '../core/tools/translate';

import HaloLayer from './HaloLayer';
import Leaflet from './Leaflet';

const LayerControls = (name) => require('../controls').get(name);
const get = (name, currentMap = worldMap.getCurrent()) => {
    return require('./index').default.get(name, currentMap);
};

const tierColors = {
    1: 'green',
    2: 'blue',
    3: 'red',
    4: 'purple'
};

export const GREENFIELD_Z_INDEX_OFFSET = -100000;

export const POINT_TYPE_COLORS = {
    '1S': '#3D7DC8',
    '2S': '#C8861E',
    '3S': '#2E9A6C'
};

class NewGreenfieldLayer {
    constructor (currentMap) {
        const mapId = +currentMap._id;

        const clusterImage = new Image();
        clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_greenfield.png`;

        const onClick = (feature, layer) => {
            LayerControls('NewGreenfield').highlight(layer);
            LayerControls('NewGreenfield').show({
                show: true,
                id: feature.properties.Id
            });

            return new HaloLayer(currentMap, layer, feature, 'NewGreenfield', 'points');
        };

        const onSuccess = (res) => {
            const shapeLayer = get('NewGreenfieldArea', currentMap);
            if (!shapeLayer.visible()) {
                shapeLayer.redraw();
            }

            return new Promise((resolve) => {
                _.forEach(res.features, f => {
                    const tier = f.properties.PointTier;

                    f.properties['Icon'] = 'pi';
                    f.properties['TierColor'] = tierColors[tier.Id] || 'orange';
                    f.properties['PinIcon'] = `${environment.get('IMAGES_HOST')}/pin_pi_${f.properties.TierColor}_xs.png`;
                    f.properties['TypeName'] = tier.Name;
                    f.properties['Legend'] = `${tier.Description || tier.Name} (Tier ${tier.Id})`;
                    f.properties['TierText'] = `${tier.Id} — ${tier.Description || tier.Name}`;
                    f.properties['PointTypeTag'] = (f.properties.PointType || {}).Name;
                    f.properties['PointTypeColor'] = POINT_TYPE_COLORS[(f.properties.PointType || {}).Name];
                });

                LayerControls('NewGreenfield').stackFeatures(res.features, currentMap);
                resolve(res);
            });
        };

        const createPopupTemplate = (properties) =>
            new Promise((resolve) => resolve($(templates('map-layer-greenfield-popup', properties))));

        const onComplete = (features) => {
            try {
                if (LayerControls('Voronoi').getControls(mapId).switch.onOff.status) { LayerControls('Voronoi').update(mapId); }
            } catch (error) {
                console.log('Layer not avaliable');
            }

            return LayerControls('NewGreenfield').show({
                show: false,
                refresh: true
            });
        };

        const onRemove = (features) => {
            try {
                if (LayerControls('Voronoi').getControls(mapId).switch.onOff.status) { return LayerControls('Voronoi').update(mapId); }

                const shapeLayer = get('NewGreenfieldArea', currentMap);
                if (shapeLayer.visible) {
                    shapeLayer.hide();
                }
            } catch (error) {
                console.log('Layer not avaliable');
            }
        };

        const onMoveend = () =>
            LayerControls('NewGreenfield').show({ refresh: true }, get('NewGreenfield', currentMap).getCurrentFeatures());

        const clusterTemplate = (cluster) => {
            const data = { value: cluster.getChildCount() };

            return pinBuilder.create.pin.imageWithValue(data, clusterImage);
        };

        return new Leaflet.GeoJSONLayer({
            map: currentMap,
            remote: {
                method: 'GET',
                url: `${environment.get('API_HOST')}/newgreenfield`,
                params: {
                    year: _.includes(environment.get('CONFIG.FILTERS.GREENFIELD_SCENARIO_YEARS'), moment().format('YYYY'))
                        ? moment().format('YYYY')
                        : _.first(environment.get('CONFIG.FILTERS.GREENFIELD_SCENARIO_YEARS'))
                }
            },
            scaleRange: [5, 20],
            filter: {
                location: true
            },
            showAll: true,
            autofocus: false,
            uniqueField: 'Id',
            sort: 'IdPointTier',
            click: onClick,
            popup: {
                template: createPopupTemplate,
                trigger: 'mouseover',
                options: {
                    offset: L.point(0, -50),
                    closeButton: false,
                    autoclose: true,
                    className: 'leaflet-popup-greenfield'
                },
                single: true
            },
            remove: onRemove,
            dragend: onMoveend,
            zoomend: onMoveend,
            done: onComplete,
            success: onSuccess,
            legend: {
                title: translate('Common.NewGreenfield'),
                subtitle: environment.get('CONFIG.FILTERS.GREENFIELD_LAST_UPDATE')
                    ? translate('Greenfield.UpdatedAt', {
                        date: _.capitalize(moment(environment.get('CONFIG.FILTERS.GREENFIELD_LAST_UPDATE')).format(translate('Greenfield.UpdatedAtFormat')))
                    })
                    : '',
                text: '{Legend}',
                order: properties => properties.PointTier.Id,
                icon: properties => `${environment.get('IMAGES_HOST')}/pin_pi_${String(properties.TierColor).toUnderscoreCase()}_xs.png`,
            },
            type: 'cluster',
            symbology: {
                type: 'polymorph',
                cluster: {
                    template: clusterTemplate
                },
                heat: {
                    theme: ColorBrewer.getHeatColors('Greens')
                },
                property: 'Icon',
                values: [{
                    value: 'pi',
                    style: {
                        icon: {
                            fields: ['TierColor'],
                            iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}.png`,
                            iconSize: [50, 64], // size of the icon
                            iconAnchor: [25, 60], // point of the icon which will correspond to marker's location
                            labelAnchor: [25, -32]
                        },
                        zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                        riseOnHover: true
                    }
                }, {
                    value: 'pi_stacked',
                    style: {
                        icon: {
                            fields: ['TierColor'],
                            iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                            iconSize: [26, 26],
                            iconAnchor: [13, -6],
                            labelAnchor: [6, 0],
                            stacked: true
                        },
                        zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                        riseOnHover: true
                    }
                }, {
                    value: 'pi_stacked_below',
                    style: {
                        icon: {
                            fields: ['TierColor'],
                            iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                            iconSize: [26, 26],
                            iconAnchor: [13, -34],
                            labelAnchor: [6, 0],
                            stacked: true
                        },
                        zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                        riseOnHover: true
                    }
                }, {
                    value: 'pi_stacked_brownfield',
                    style: {
                        icon: {
                            fields: ['TierColor'],
                            iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                            iconSize: [26, 26],
                            iconAnchor: [13, -6],
                            labelAnchor: [6, 0],
                            stacked: true
                        },
                        zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                        riseOnHover: true
                    }
                }]
            }
        });
    }
}

export default NewGreenfieldLayer;



// WEBPACK FOOTER //
// ./src/scripts/layers/NewGreenfieldLayer.js